// utils/transpose.ts
// Transpose utility functions for shifting guitar tunings and key centers

import { NOTES } from './constants';
import type { Note, GuitarArrangement } from '../presets/presetSchema';

/**
 * Transpose a note by a given number of semitones
 * @param note - The original note (e.g., "C", "F#")
 * @param semitones - Number of semitones to transpose (can be negative)
 * @returns The transposed note
 */
export function transposeNote(note: Note, semitones: number): Note {
  const noteIndex = NOTES.indexOf(note);
  if (noteIndex === -1) {
    // Return original if invalid
    return note;
  }
  
  // Calculate new index with proper modulo handling for negative numbers
  const newIndex = ((noteIndex + semitones) % 12 + 12) % 12;
  return NOTES[newIndex];
}

/**
 * Calculate semitone difference between two notes
 * @param fromNote - Starting note
 * @param toNote - Target note
 * @returns Number of semitones difference (0-11)
 */
export function calculateSemitoneDistance(fromNote: Note, toNote: Note): number {
  const fromIndex = NOTES.indexOf(fromNote);
  const toIndex = NOTES.indexOf(toNote);
  
  if (fromIndex === -1 || toIndex === -1) {
    return 0;
  }
  
  // Calculate the shortest distance (always positive, 0-11)
  const distance = (toIndex - fromIndex + 12) % 12;
  return distance;
}

/**
 * Transpose all guitars in a guitars array by a given number of semitones
 * @param guitars - Array of guitar objects
 * @param semitones - Number of semitones to transpose
 * @returns New array of transposed guitars
 */
export function transposeGuitars(guitars: GuitarArrangement[], semitones: number): GuitarArrangement[] {
  if (semitones === 0) return guitars; // No change needed
  
  return guitars.map(guitar => ({
    ...guitar,
    root: {
      ...guitar.root,
      note: transposeNote(guitar.root.note, semitones)
    },
    // Also transpose overlay note if it exists
    ...(guitar.overlay && {
      overlay: {
        ...guitar.overlay,
        note: transposeNote(guitar.overlay.note, semitones)
      }
    })
  }));
}

/**
 * Get the current key center from the first guitar (reference guitar)
 * @param guitars - Array of guitar objects
 * @returns The root note of the first guitar, or null if no guitars
 */
export function getCurrentKeyCenter(guitars: GuitarArrangement[]): Note | null {
  if (!guitars || guitars.length === 0) {
    return null;
  }
  return guitars[0].root.note;
}

/**
 * Generate transpose display text showing key movement
 * @param fromKey - Original key center
 * @param toKey - Target key center
 * @returns Display text like "C → G (+7)" or "C → F (-7)"
 */
export function getTransposeDisplayText(fromKey: Note, toKey: Note): string {
  if (!fromKey || !toKey) return '';
  if (fromKey === toKey) return `${fromKey} (no change)`;
  
  const semitones = calculateSemitoneDistance(fromKey, toKey);
  // Show negative for down transpositions (more than 6 semitones up is easier shown as down)
  const displaySemitones = semitones > 6 ? semitones - 12 : semitones;
  const sign = displaySemitones > 0 ? '+' : ''; // Don't show + for 0, - is automatic
  
  return `${fromKey} → ${toKey} (${sign}${displaySemitones})`;
}
